'use client';

import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Minus, Plus } from 'lucide-react';
import { tw } from '@tw';
import { PRICE_MAX, PRICE_MIN, priceProblem } from '@peach/types/payments/price-limits.js';
import { BundleType, type BundleDraft } from '@/components/products/bundle-config';
import { shownDealPrice } from '@/components/products/pricing';
import { lengthLimits, priceStep } from '@/components/products/terms-fields';
import { useBundlesStore } from '@/components/products/use-bundles-store';
import { useNumberDraft } from '@/components/products/use-number-draft';
import { useOwnerCatalogue } from '@/components/products/use-owner-catalogue';
import { formatPrice } from '@/lib/utils/format';
import { OWNER_TRIAL_LINE } from '../intro-copy';
import { dealNote, priceSplitNote, withFees } from '@/components/products/price-copy';
import s from './sub.module.css';

/**
 * The owner's intro call on a subscription page, in the page's own design: at
 * rest it reads as a client reads it, Edit opens the terms in place. The page
 * sells no packs, so the catalogue the store saves is the trial alone.
 */
export function IntroEditor({
  bundles,
  currency,
  username,
  children,
}: {
  /** The owner's catalogue as the server has it: the trial, or nothing. */
  bundles: BundleDraft[];
  currency: string;
  username: string;
  /** What a client reads under the price at rest (the steps). */
  children?: ReactNode;
}) {
  const seeded = useOwnerCatalogue(bundles, currency, username);
  const stored = useBundlesStore((st) => st.bundles.find((b) => b.type === BundleType.TRIAL));
  const editingId = useBundlesStore((st) => st.editingId);
  const saving = useBundlesStore((st) => st.saving);
  const edit = useBundlesStore((st) => st.edit);
  const add = useBundlesStore((st) => st.add);
  const cancel = useBundlesStore((st) => st.cancel);
  const save = useBundlesStore((st) => st.save);

  // Before the store is seeded it is empty; the server's copy stands in for that frame.
  const intro = seeded ? stored : bundles.find((b) => b.type === BundleType.TRIAL);
  const open = !!intro && editingId === intro.id;

  const root = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      root.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      root.current?.querySelector('input')?.focus({ preventScroll: true });
    } else if (!open && wasOpen.current) opener.current?.focus();
    wasOpen.current = open;
  }, [open]);

  if (!intro) {
    return (
      <button
        ref={opener}
        type="button"
        onClick={() => add(BundleType.TRIAL)}
        disabled={!seeded || saving}
        className={tw(s.ieAction, s.ieAdd, s.press)}
      >
        Add an intro call
      </button>
    );
  }

  if (!open) {
    return (
      <>
        <IntroLine
          duration={intro.duration}
          price={intro.price}
          previousPrice={intro.deal ? intro.originalPrice : null}
          currency={currency}
        />
        {children}
        <button
          ref={opener}
          type="button"
          onClick={() => edit(intro.id)}
          disabled={editingId !== null || saving}
          className={tw(s.ieAction, s.press)}
        >
          Edit
        </button>
      </>
    );
  }

  return (
    <div
      ref={root}
      className={s.ieForm}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !e.defaultPrevented && !saving) cancel();
      }}
    >
      <h3 className={s.ieTitle}>{OWNER_TRIAL_LINE}</h3>
      <IntroFields intro={intro} />
      <div className={s.iePreview}>
        <span className={s.ieCaption}>What clients see</span>
        <IntroLine
          duration={intro.duration}
          price={intro.price}
          previousPrice={intro.deal ? intro.originalPrice : null}
          currency={currency}
        />
      </div>
      <p className={s.ieNote}>{priceSplitNote(intro.price, currency)}</p>
      <div className={s.ieButtons}>
        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          className={tw(s.ieAction, s.press)}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className={tw(s.ieAction, s.ieSave, s.press)}
        >
          {saving ? 'Saving' : 'Save'}
        </button>
      </div>
      <SaveError id={intro.id} />
    </div>
  );
}

/** The intro as a client reads it: its length, then its price with fees, a deal's struck over it. */
export function IntroLine({
  duration,
  price,
  previousPrice,
  currency,
}: {
  duration: number;
  price: number;
  previousPrice?: number | null;
  currency: string;
}) {
  const money = (amount: number) => formatPrice({ amount, currency });
  return (
    <div className={s.introHead}>
      <b>Intro call, {duration} min</b>
      <span className={s.iePrice}>
        {previousPrice != null && previousPrice > price && <s>{money(withFees(previousPrice))}</s>}
        <b>{money(withFees(price))}</b>
      </span>
    </div>
  );
}

function IntroFields({ intro }: { intro: BundleDraft }) {
  const setTerms = useBundlesStore((st) => st.setTerms);
  const saved = useBundlesStore((st) => st.before?.find((b) => b.id === intro.id));
  const refused = useBundlesStore((st) => st.errors[intro.id] ?? null);
  const currency = useBundlesStore((st) => st.currency);
  const dealId = useId();
  const deal = dealNote(intro.deal, shownDealPrice(intro, saved), currency);

  return (
    <div className={s.ieFields}>
      <Stepper
        label="Length"
        unit="min"
        value={intro.duration}
        {...lengthLimits(intro.type)}
        step={1}
        onCommit={(duration) => setTerms(intro.id, { duration })}
      />
      <Stepper
        label="Price"
        prefix="$"
        value={intro.price}
        min={PRICE_MIN}
        max={PRICE_MAX}
        step={priceStep(intro.type)}
        onCommit={(price) => setTerms(intro.id, { price })}
        problem={(typed) => (typed === null ? null : priceProblem(typed)) ?? refused}
      />
      <div className={s.ieRow}>
        <span className={s.ieLabel}>
          <span id={dealId}>Make it a deal</span>
          <small>{deal}</small>
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={intro.deal}
          aria-labelledby={dealId}
          onClick={() => setTerms(intro.id, { deal: !intro.deal })}
          className={s.ieSwitch}
        >
          <span />
        </button>
      </div>
    </div>
  );
}

/** A number typed or stepped; a price out of range says why under it while it is typed. */
function Stepper({
  label,
  unit,
  prefix,
  value,
  min,
  max,
  step,
  onCommit,
  problem,
}: {
  label: string;
  unit?: string;
  prefix?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onCommit: (next: number) => void;
  problem?: (typed: number | null) => string | null;
}) {
  const id = useId();
  const field = useNumberDraft({ value, min, max, step, onCommit });
  const why = problem?.(field.typed) ?? null;

  return (
    <div className={s.ieRow}>
      <label htmlFor={id} className={s.ieLabel}>
        {label}
      </label>
      <div className={s.ieStepper}>
        {/* aria-disabled, never disabled: a clamp on blur can reach the limit just as
            focus lands here, and a disabled button drops focus to the body. */}
        <button
          type="button"
          aria-label={`${label}: less`}
          aria-disabled={field.atMin}
          onClick={() => !field.atMin && field.less()}
          className={tw(s.ieStep, s.press)}
        >
          <Minus size={18} aria-hidden="true" />
        </button>
        <span className={tw(s.ieBox, why && s.ieBoxBad)}>
          {prefix && <span aria-hidden="true">{prefix}</span>}
          <input
            id={id}
            inputMode="numeric"
            value={field.text}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
            onKeyDown={field.onKeyDown}
            size={Math.max(field.text.length, 2)}
            aria-invalid={!!why}
            aria-describedby={why ? `${id}-why` : undefined}
          />
          {unit && <span aria-hidden="true">{unit}</span>}
        </span>
        <button
          type="button"
          aria-label={`${label}: more`}
          aria-disabled={field.atMax}
          onClick={() => !field.atMax && field.more()}
          className={tw(s.ieStep, s.press)}
        >
          <Plus size={18} aria-hidden="true" />
        </button>
      </div>
      {why && (
        <span id={`${id}-why`} role="status" className={s.ieWhy}>
          {why}
        </span>
      )}
    </div>
  );
}

/** The server's refusal (an empty week under live products, say), where Save was pressed. */
function SaveError({ id }: { id: string }) {
  const general = useBundlesStore((st) => st.errors.general ?? null);
  const deal = useBundlesStore((st) => st.errors[`original-${id}`] ?? null);
  const error = general ?? deal;
  return error ? (
    <span role="status" className={s.error}>
      {error}
    </span>
  ) : null;
}
